import type { TicketListParams } from "./api";

/**
 * Query key factory.
 *
 * Centralised because the socket layer invalidates and patches these caches by
 * key, and a key built slightly differently in two places produces a cache
 * entry the realtime updates never reach -- which presents as "updates work on
 * some screens but not others".
 */
export const queryKeys = {
  me: ["me"] as const,
  channels: ["channels"] as const,
  channelConnections: ["channel-connections"] as const,
  channelAlerts: ["channels", "alerts"] as const,
  failedReplies: ["channels", "failed-replies"] as const,
  ebayConnections: ["channel-connections", "ebay"] as const,
  users: ["users"] as const,
  viewCounts: ["tickets", "view-counts"] as const,
  canned: ["canned-responses"] as const,
  dashboardFilters: ["dashboard", "filters"] as const,
  dashboard: (filters: { teamId?: number; userId?: number }) =>
    ["dashboard", "highlights", filters] as const,
  ticketReport: (filters: { teamId?: number; userId?: number }) =>
    ["dashboard", "ticket-report", filters] as const,
  /* Report cards key on their own range as well as the scope, because each one
     carries its own filter and two cards on the same page routinely disagree
     about which period they are showing. */
  dashboardReport: (
    report: string,
    range: unknown,
    scope: unknown,
    extra?: unknown,
  ) => ["dashboard", report, range, scope, extra] as const,
  dashboardKpiMetrics: ["dashboard", "kpi", "metrics"] as const,
  dashboardLayout: (viewKey: string) =>
    ["dashboard", "layout", viewKey] as const,
  dashboardTemplates: ["dashboard", "templates"] as const,
  automations: ["automations"] as const,
  pipelines: ["settings", "pipelines"] as const,
  teams: ["settings", "teams"] as const,
  signatures: ["settings", "signatures"] as const,
  agents: ["settings", "agents"] as const,
  mySettings: ["me", "settings"] as const,
  notificationPreferences: ["me", "notification-preferences"] as const,
  /**
   * Push, for this browser.
   *
   * Not part of `notificationPreferences` even though it sits in the same
   * panel: that one is an account-level answer the server owns outright, while
   * this one is half browser state and cannot be refetched into correctness.
   */
  pushState: ["me", "push"] as const,
  notifications: ["notifications"] as const,
  /** Prefix over every tab of the history, for patching a row read in all. */
  notificationHistoryRoot: ["notifications", "history"] as const,
  notificationHistory: (product: string | null) =>
    ["notifications", "history", product] as const,
  ticketWatchers: (ticketId: number) =>
    ["tickets", ticketId, "watchers"] as const,
  apiKeys: ["me", "api-keys"] as const,
  savedViews: ["saved-views"] as const,
  favourites: ["favourites"] as const,
  allowedSenders: ["settings", "allowed-senders"] as const,
  brandImages: ["settings", "images"] as const,
  sendFrom: ["tickets", "send-from"] as const,
  whatsappWindow: (id: number) => ["tickets", id, "whatsapp-window"] as const,
  /** Per alias: the fallback signature differs by channel. */
  mySignature: (channelId?: number) =>
    ["me", "signature", channelId ?? null] as const,
  sendFromGrants: ["settings", "send-from"] as const,
  businessHours: ["settings", "business-hours"] as const,
  /* Not under "settings": opening hours are configuration an admin edits, and
     this is what the desk is doing today. They expire on completely different
     timescales and invalidating one must not drop the other. */
  away: ["away"] as const,

  /** Prefix matching every ticket-list query, whatever its filters. */
  ticketsRoot: ["tickets", "list"] as const,

  tickets: (params: TicketListParams) => ["tickets", "list", params] as const,

  /**
   * Prefix over every open ticket's detail.
   *
   * For the things a ticket copies from somewhere else -- the channel's display
   * name is the current one -- where a change at the source has to reach every
   * detail already in the cache, not just the one on screen.
   */
  ticketDetailRoot: ["tickets", "detail"] as const,

  ticket: (id: number) => ["tickets", "detail", id] as const,
  summary: (id: number) => ["ticket-summary", id] as const,
  laneLink: (id: number) => ["lane-link", id] as const,
  laneStatus: ["lane-status"] as const,
  laneConnection: ["lane-connection"] as const,
  /** The agent's own Google grant: their calendar, and their own mail. */
  googleConnection: ["google-connection"] as const,
  laneBoards: ["lane-boards"] as const,
  laneMembers: ["lane-members"] as const,
  related: (id: number) => ["tickets", "related", id] as const,

  // ── CRM ───────────────────────────────────────────────────────────────────

  /** Prefix matching every company-list query, whatever its filters. */
  crmCompaniesRoot: ["crm", "companies"] as const,

  crmCompanies: (params: Record<string, unknown>) =>
    ["crm", "companies", params] as const,
  crmCompany: (id: number) => ["crm", "company", id] as const,
  crmFilters: ["crm", "filters"] as const,
  /** Per user, so it is keyed by view only -- the session decides whose. */
  crmFilterBar: (viewKey: string) => ["crm", "filter-bar", viewKey] as const,
  /** Includes the branch scope: the same filters over a group are a different list. */
  crmTimeline: (id: number, kinds: string[], includeBranches: boolean) =>
    ["crm", "timeline", id, kinds, includeBranches] as const,
  /**
   * The quotes-and-orders panels' own read of the same rows.
   *
   * Deliberately *not* `crmTimeline(id, ["quote", "order"], false)`, which is
   * what the timeline's "Quotes & orders" chip builds. Both read the same
   * activities, but the panels use a plain query and the timeline an infinite
   * one, and a cache entry holds whichever shape wrote it last: with one key,
   * clicking the chip handed `useInfiniteQuery` the panels' bare
   * `TimelinePage`, `data.pages` was `undefined`, and the company page went
   * white on `data.pages.flatMap`.
   *
   * It keeps the `["crm", "timeline", id]` prefix on purpose, so recording a
   * quote still invalidates the panels along with the timeline.
   */
  crmCommercialPanels: (id: number) =>
    ["crm", "timeline", id, "commercial-panels"] as const,
  /** One person's own history, as the Helpdesk's contact drawer reads it. */
  crmContact: (id: number) => ["crm", "contact", id] as const,
  crmContactTimeline: (id: number, kinds: string[]) =>
    ["crm", "contact-timeline", id, kinds] as const,
  crmOwnerSuggestion: (id: number) => ["crm", "owner-suggestion", id] as const,
  crmRollup: (id: number) => ["crm", "rollup", id] as const,
  /** Per company: the default assignee depends on who manages the account. */
  crmLaneBoards: (companyId?: number) =>
    ["crm", "lane-boards", companyId ?? null] as const,
  crmListView: (viewKey: string) => ["crm", "view", viewKey] as const,
  /** Every team's stored layout, for the settings editor. */
  crmTeamListViews: (viewKey: string) =>
    ["crm", "view", viewKey, "teams"] as const,
  crmContactMethod: (id: number) => ["crm", "contact-method", id] as const,
  crmSearch: (q: string) => ["crm", "search", q] as const,
  /* The platform box, which spans all four products -- hence a key outside any
     one of their namespaces. */
  globalSearch: (q: string) => ["search", q] as const,
  crmByDomain: (domain: string, name: string) =>
    ["crm", "by-domain", domain, name] as const,
  crmSuggest: (domain: string, name: string) =>
    ["crm", "suggest", domain, name] as const,
  crmDuplicates: (status: string) => ["crm", "duplicates", status] as const,
  crmArchived: ["crm", "archive", "list"] as const,
  crmArchiveCandidates: (months: number) => ["crm", "archive", "candidates", months] as const,
  crmFields: ["crm", "fields"] as const,
  /** Built-ins, custom fields and picklists together, for the field editor. */
  crmFieldSettings: ["crm", "field-settings"] as const,
  crmQuickTasks: ["crm", "quick-tasks"] as const,
  crmTaskRules: ["crm", "task-rules"] as const,
  /** Recomputed per company on every read, so it is deliberately short-lived. */
  crmTaskSuggestions: (id: number) => ["crm", "task-suggestions", id] as const,
  /** Per company and per person, since the answer is "what do *I* follow". */
  crmCompanyFollow: (id: number) => ["crm", "company-follow", id] as const,
  crmPicklists: ["crm", "picklists"] as const,
  crmQuotes: (id: number) => ["crm", "quotes", id] as const,
  crmOrders: (id: number) => ["crm", "orders", id] as const,
  /**
   * Admins only. Not refreshed when an order is typed in -- like `crmOrders`,
   * it catches up on its stale time -- because nearly every order arrives from
   * the platform rather than from this page.
   */
  crmPerformance: (id: number) => ["crm", "performance", id] as const,

  /** Whether a credit bureau is connected at all. Server-wide, so unkeyed. */
  crmCreditsafeStatus: ["crm", "creditsafe-status"] as const,
  /**
   * A bureau search, keyed by its whole criteria object.
   *
   * Searching is free at Creditsafe's end, so caching this is about not
   * re-running the same query while somebody scrolls the results -- not about
   * saving money, which is what the report cache is for.
   */
  crmCreditsafeSearch: (criteria: Record<string, unknown>) =>
    ["crm", "creditsafe-search", criteria] as const,
  /** A company's stored credit file. Never triggers an order. */
  crmCredit: (id: number) => ["crm", "credit", id] as const,
  crmCreditHistory: (id: number) => ["crm", "credit-history", id] as const,
  /**
   * The AM dashboard. Scope is in the key so switching tabs refetches rather
   * than showing the previous person's book while the new one loads.
   */
  crmAmScopes: ["crm", "am-dashboard", "scopes"] as const,
  crmAmDashboard: (scope: string) =>
    ["crm", "am-dashboard", "overview", scope] as const,
  crmAmCustomers: (
    scope: string,
    filter?: string,
    sort?: string,
    owner?: string,
  ) =>
    [
      "crm",
      "am-dashboard",
      "customers",
      scope,
      filter ?? "",
      sort ?? "",
      owner ?? "",
    ] as const,
  crmAmSettings: ["crm", "am-dashboard", "settings"] as const,

  crmWebhooks: ["crm", "webhooks"] as const,
  /** Per endpoint: the recent-deliveries panel opens one at a time. */
  crmWebhookDeliveries: (id: number) =>
    ["crm", "webhooks", "deliveries", id] as const,

  // ── Sales calls ───────────────────────────────────────────────────────────

  /**
   * Prefix matching everything in the module.
   *
   * Every write here moves more than one number -- claiming a call empties a
   * slot in the pool, changes the "mine" count and reorders the worklist -- so
   * mutations invalidate this root rather than trying to name the four keys
   * they touched and inevitably missing one.
   */
  salesRoot: ["sales"] as const,

  /*
   * The phone system's history. One root, because a sync changes every list
   * and the status line together; the dashboard's call figures are under
   * `dashboardReport` with the other cards, so they share its refresh clock.
   */
  phoneCallsRoot: ["phone-calls"] as const,
  phoneCalls: (params: Record<string, unknown>) =>
    ["phone-calls", "list", params] as const,
  phoneCall: (id: number) => ["phone-calls", "call", id] as const,
  phoneCallTranscription: (id: number) =>
    ["phone-calls", "transcription", id] as const,
  phoneCallsStatus: ["phone-calls", "status"] as const,
  /* Under the root, so a sync-now also refreshes it -- and invalidated by a
     link changing in Settings, since each row says who it is linked to. */
  sipUsers: ["phone-calls", "sip-users"] as const,

  salesCalls: (params: Record<string, unknown>) =>
    ["sales", "calls", params] as const,
  salesCall: (id: number) => ["sales", "call", id] as const,
  salesBoard: ["sales", "board"] as const,
  salesConfig: ["sales", "config"] as const,
  salesReport: (range: string) => ["sales", "report", range] as const,
  /** Every call ever raised against one account, for the CRM's company page. */
  salesForCompany: (companyId: number) =>
    ["sales", "company", companyId] as const,

  /*
   * The diary. Its own product in the rail, but kept under the same `sales`
   * root here so the existing invalidation covers it: writing up a meeting
   * moves it between two of the three lists and changes the sidebar's counts in
   * the same step, and booking one off a call has to refresh both products at
   * once. Naming those keys one by one is how one of them gets missed.
   */
  salesMeetings: (params: Record<string, unknown>) =>
    ["sales", "meetings", params] as const,
  salesMeeting: (id: number) => ["sales", "meeting", id] as const,
  salesMeetingCounts: ["sales", "meeting-counts"] as const,
  salesMeetingsForCompany: (companyId: number) =>
    ["sales", "company-meetings", companyId] as const,
};
